import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import { Plus, Edit, Trash2, Building2, Search, CalendarClock } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Amenities.module.css';

const TimePicker = ({ value, onChange }) => {
  let h = '09', m = '00', ampm = 'AM';
  if (value && value.includes(':')) {
    const parts = value.split(' ');
    const timeParts = parts[0].split(':');
    h = timeParts[0];
    m = timeParts[1] || '00';
    ampm = parts[1] || 'AM';
  }

  const hours = Array.from({length: 12}, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', width: '100%' }}>
      <select 
        value={h} 
        onChange={(e) => onChange(`${e.target.value}:${m} ${ampm}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer' }}
      >
        {hours.map(hr => <option key={hr} value={hr}>{hr}</option>)}
      </select>
      <span style={{ fontWeight: 'bold', color: '#64748b' }}>:</span>
      <select 
        value={m} 
        onChange={(e) => onChange(`${h}:${e.target.value} ${ampm}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer' }}
      >
        {!minutes.includes(m) && <option value={m}>{m}</option>}
        {minutes.map(min => <option key={min} value={min}>{min}</option>)}
      </select>
      <select 
        value={ampm} 
        onChange={(e) => onChange(`${h}:${m} ${e.target.value}`)} 
        style={{ padding: '10px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1, backgroundColor: '#f8fafc', fontSize: '14px', fontWeight: '600', color: '#0f172a', cursor: 'pointer' }}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const Amenities = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAmenities();
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const { data } = await api.get('/admin/amenity-bookings');
      setBookings(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAmenities = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/amenities');
      setAmenities(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const [formState, setFormState] = useState({
    name: '', type: 'Hall', capacity: '', startTime: '06:00 AM', endTime: '10:00 PM', status: 'Active'
  });
  const [customType, setCustomType] = useState('');
  const [imageFile, setImageFile] = useState(null);

  const handleChange = (e) => setFormState({ ...formState, [e.target.name]: e.target.value });

  const openDrawerForAdd = () => {
    setEditingId(null);
    setFormState({ name: '', type: 'Hall', capacity: '', startTime: '06:00 AM', endTime: '10:00 PM', status: 'Active' });
    setCustomType('');
    setImageFile(null);
    setIsDrawerOpen(true);
  };

  const handleEditClick = (item) => {
    setEditingId(item._id);
    let sTime = '06:00 AM';
    let eTime = '10:00 PM';
    if (item.timings) {
      const parts = item.timings.split(' - ');
      if (parts.length === 2) { sTime = parts[0]; eTime = parts[1]; }
    }
    
    const standardTypes = ['Sports', 'Hall', 'Pool', 'Gym', 'Other'];
    const isCustom = !standardTypes.includes(item.type);
    
    setFormState({ 
      name: item.name, 
      type: isCustom ? 'Other' : (item.type || 'Hall'), 
      capacity: item.capacity, 
      startTime: sTime, 
      endTime: eTime, 
      status: item.status 
    });
    setCustomType(isCustom ? item.type : '');
    setImageFile(null);
    setIsDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();
      submitData.append('name', formState.name);
      submitData.append('type', formState.type === 'Other' ? customType : formState.type);
      submitData.append('capacity', formState.capacity);
      submitData.append('timings', `${formState.startTime} - ${formState.endTime}`);
      submitData.append('status', formState.status);
      if (imageFile) submitData.append('image', imageFile);

      if (editingId) {
        const { data } = await api.put(`/admin/amenities/${editingId}`, submitData);
        setAmenities(amenities.map(a => a._id === editingId ? data : a));
        toast.success('Amenity updated successfully');
      } else {
        const { data } = await api.post('/admin/amenities', submitData);
        setAmenities([data, ...amenities]);
        toast.success('Amenity added successfully');
      }
      setIsDrawerOpen(false);
      setEditingId(null);
    } catch (err) {
      toast.error(editingId ? 'Failed to update amenity' : 'Failed to add amenity');
    }
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to delete this amenity?", async () => {
      try {
        await api.delete(`/admin/amenities/${id}`);
        setAmenities(amenities.filter(item => item._id !== id));
        toast.success('Amenity deleted successfully');
      } catch (err) {
        toast.error('Failed to delete amenity');
      }
    });
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Facilities & Amenities" 
        breadcrumb={['Society Management', 'Amenities & Bookings']} 
        actionButton={
          <button className={styles.addBtn} onClick={openDrawerForAdd}>
            <Plus size={16} /> Add Amenity
          </button>
        }
      />

      <div className={styles.bookingsCard}>
        <div className={styles.cardHeader}>
          <h3><CalendarClock size={18} /> Today's Bookings</h3>
        </div>
        <div className={styles.bookingsList}>
          {bookings.length === 0 ? <p style={{padding: '0 24px'}}>No bookings available.</p> : bookings.map(b => (
            <div key={b._id} className={styles.bookingItem}>
              <div className={styles.bkInfo}>
                <strong>{b.amenity}</strong>
                <span>Booked by {b.resident ? `${b.resident.wing || ''}-${b.resident.flatNumber || ''} (${b.resident.fullName || ''})` : 'Unknown'}</span>
              </div>
              <div className={styles.bkTime}>
                <span className={styles.timeSlot}>{b.date} | {b.timeSlot}</span>
                <span className={styles[`badge${b.status}`] || styles.badgeConfirmed}>{b.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.grid}>
        {loading ? <p>Loading amenities...</p> : amenities.map(item => (
          <div key={item._id} className={styles.card}>
            <div className={styles.iconWrapper}>
              <Building2 size={24} />
            </div>
            <div className={styles.cardInfo}>
              <h3>{item.name}</h3>
              <div className={styles.metaRow}>
                <span>Type: {item.type}</span>
                <span>Max: {item.capacity} Pax</span>
              </div>
              <small>Timings: {item.timings}</small>
            </div>
            <div className={styles.cardFooter}>
              <span className={`${styles.statusBadge} ${styles[item.status.toLowerCase()]}`}>
                {item.status}
              </span>
              <div className={styles.actionBtns}>
                <button className={styles.btnIcon} onClick={() => handleEditClick(item)}><Edit size={14} /></button>
                <button className={`${styles.btnIcon} ${styles.danger}`} onClick={() => handleDelete(item._id)}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title={editingId ? "Edit Amenity" : "Add Amenity"}>
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Amenity Name</label>
            <input name="name" value={formState.name} onChange={handleChange} required placeholder="e.g. Tennis Court" />
          </div>
          <div className="formGroup">
            <label>Category Type</label>
            <select name="type" value={formState.type} onChange={handleChange}>
              <option value="Sports">Sports</option>
              <option value="Hall">Hall / Banquet</option>
              <option value="Pool">Swimming Pool</option>
              <option value="Gym">Gymnasium</option>
              <option value="Other">Other (Custom)</option>
            </select>
          </div>
          {formState.type === 'Other' && (
            <div className="formGroup">
              <label>Custom Category Name</label>
              <input value={customType} onChange={(e) => setCustomType(e.target.value)} required placeholder="e.g. Library" />
            </div>
          )}
          <div className="formGroup">
            <label>Max Capacity</label>
            <input type="number" name="capacity" value={formState.capacity} onChange={handleChange} required placeholder="Number of people" />
          </div>
          <div className="formGroup" style={{display: 'flex', gap: '16px'}}>
            <div style={{flex: 1}}>
              <label>Start Time</label>
              <TimePicker value={formState.startTime} onChange={(val) => setFormState({...formState, startTime: val})} />
            </div>
            <div style={{flex: 1}}>
              <label>End Time</label>
              <TimePicker value={formState.endTime} onChange={(val) => setFormState({...formState, endTime: val})} />
            </div>
          </div>
          <div className="formGroup">
            <label>Status</label>
            <select name="status" value={formState.status} onChange={handleChange}>
              <option value="Active">Active</option>
              <option value="Maintenance">Under Maintenance</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Amenity Image (Optional)</label>
            <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">{editingId ? "Update Amenity" : "Save Amenity"}</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Amenities;
